import { useCallback, useRef, useState } from 'react';
import { useSetAtom } from 'jotai';
import request from 'lib/request';
import { unreadCountAtom, unseenCountAtom, notificationsAtom } from './atoms';
import type { NotificationRow, NotificationsQueryResponse } from './types';

const QUERY_PATH = '/queries/notifications';

/** The dropdown's worth. The full page asks for more. */
export const PANEL_PAGE_SIZE = 20;

interface FetchOptions {
  unreadOnly?: boolean;
  page?: number;
  pageSize?: number;
  /** Notification::KINDS values. Empty or omitted means every kind. */
  kinds?: string[];
  /** Matches the title or the Jira key on the row. Omitted or blank means no narrowing. */
  text?: string;
  /** Whole-day bounds, 'YYYY-MM-DD'. Either end may be omitted. */
  createdAt?: { start?: string; end?: string };
}

export interface UseNotificationsReturn {
  rows: NotificationRow[];
  totalCount: number;
  isLoading: boolean;
  error: string | null;
  /** Pusher channel for live updates. Null until the first response names it. */
  channel: string | null;
  /** Resolves with the rows it delivered, so a caller can act on exactly those. */
  fetchPage: (options?: FetchOptions) => Promise<NotificationRow[]>;
  markAllSeen: (ids: number[]) => Promise<void>;
  markRead: (id: number) => Promise<void>;
  markAllRead: () => Promise<void>;
}

/**
 * Loads and mutates notifications.
 *
 * The unread count is written to a shared atom rather than returned, because the badge that
 * displays it is not in this hook's tree — the bell lives in the app header while the panel
 * and the full page each mount their own copy of this hook.
 *
 * `syncGlobal` is false for the full page: it pages and filters, so its rows are not the
 * dropdown's rows and writing them to the shared atom would make opening the bell show
 * whatever page you happened to be on.
 *
 * @param {boolean} [syncGlobal] - Whether to publish the unread count to the shared atom.
 * @return {UseNotificationsReturn} Rows, load state, and the mark-read mutations.
 */
export function useNotifications(syncGlobal = true): UseNotificationsReturn {
  const [rows, setRows] = useState<NotificationRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [channel, setChannel] = useState<string | null>(null);

  const setUnreadCount = useSetAtom(unreadCountAtom);
  const setUnseenCount = useSetAtom(unseenCountAtom);
  const setSharedRows = useSetAtom(notificationsAtom);

  const abortRef = useRef<AbortController | null>(null);

  // markRead needs to know whether a row was unread before it flips it, and reading that
  // from `rows` would put the whole list in the callback's deps — enough to re-create
  // markRead on every fetch and re-render every row that takes it as a prop.
  const rowsRef = useRef<NotificationRow[]>([]);
  rowsRef.current = rows;

  const fetchPage = useCallback(
    async (options: FetchOptions = {}) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsLoading(true);

      const query: Record<string, unknown> = {
        page: options.page ?? 1,
        page_size: options.pageSize ?? PANEL_PAGE_SIZE,
      };
      if (options.unreadOnly) query.unread_only = true;
      if (options.kinds?.length) query.kinds = options.kinds;
      // Sent only when there is something to match, so a cleared box is the same request as
      // one that never had a search in it.
      if (options.text?.trim()) query.text = options.text.trim();
      // Sent only when a bound is actually set: an empty pair would still reach the
      // date validation and fail it.
      if (options.createdAt?.start || options.createdAt?.end) {
        query.created_at = {
          start: options.createdAt.start ?? '',
          end: options.createdAt.end ?? '',
        };
      }

      try {
        const response = await request.post<
          NotificationsQueryResponse,
          { message?: string }
        >(QUERY_PATH, { query }, controller);

        if (controller.signal.aborted || response.type === 'abort') return [];

        if (response.type !== 'success') {
          setError(
            response.type === 'error'
              ? (response.data?.message ?? 'Request failed')
              : response.message
          );
          return [];
        }

        const results = response.data.results ?? [];
        setRows(results);
        setTotalCount(response.data.totalCount ?? 0);
        // Always trusted, even when this hook is not the shared one: the count is
        // whole-set, so it is correct no matter which page asked for it.
        setUnreadCount(response.data.unreadCount ?? 0);
        setUnseenCount(response.data.unseenCount ?? 0);
        if (response.data.channel) setChannel(response.data.channel);
        if (syncGlobal) setSharedRows(results);
        setError(null);
        return results;
      } catch (_e) {
        if (!controller.signal.aborted)
          setError('Failed to load notifications');
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [setUnreadCount, setUnseenCount, setSharedRows, syncGlobal]
  );

  /**
   * Optimistic: the row is greyed the instant it is clicked, because the click also
   * navigates away and waiting for the round trip would mean the change is never seen. The
   * server's count is authoritative once it answers.
   */
  const markRead = useCallback(
    async (id: number) => {
      // Only an actually-unread row is worth touching. Clicking one that is already read
      // used to decrement the badge anyway, so re-opening a read notification walked the
      // count down past what the server thinks is unread.
      const target = rowsRef.current.find((row) => row.id === id);
      if (target && target.read) return;

      const markLocally = (read: boolean) => {
        setRows((current) =>
          current.map((row) => (row.id === id ? { ...row, read } : row))
        );
        setSharedRows((current) =>
          current.map((row) => (row.id === id ? { ...row, read } : row))
        );
      };

      markLocally(true);
      setUnreadCount((count) => Math.max(0, count - 1));

      const controller = new AbortController();
      const response = await request.patch<
        { unreadCount: number },
        { message?: string }
      >(`/notifications/${id}`, {}, controller);

      if (response.type === 'success' && response.data) {
        setUnreadCount(response.data.unreadCount);
        return;
      }

      // The optimism was wrong. Put the row and the badge back rather than leaving a
      // notification greyed out and uncounted until the next reload — the click usually
      // navigates away, so the next reload can be a long time coming.
      if (response.type === 'abort') return;
      markLocally(false);
      setUnreadCount((count) => count + 1);
    },
    [setSharedRows, setUnreadCount]
  );

  /**
   * Silences the badge for the rows the panel actually showed, without marking anything
   * read. Called when the panel opens, which is the moment the badge has done its job.
   *
   * Scoped to the delivered ids rather than every unseen row: the panel holds 20, so
   * clearing the whole table would silence the badge for older rows the user never saw.
   * The badge therefore survives an open when there is more waiting behind it, and the
   * count comes back from the server rather than being assumed to be zero.
   */
  const markAllSeen = useCallback(
    async (ids: number[]) => {
      if (ids.length === 0) return;

      const controller = new AbortController();
      const response = await request.post<{ unseenCount: number }, unknown>(
        '/notifications/seen_all',
        { ids: ids },
        controller
      );

      if (response.type === 'success' && response.data) {
        setUnseenCount(response.data.unseenCount);
      }
    },
    [setUnseenCount]
  );

  /**
   * Marks everything read, optimistically, and puts it back if the server disagrees.
   *
   * The rollback is the point, and it is what this was missing: the rows and both counters
   * were cleared and the response then thrown away, so a 500 or an expired session left a
   * cleared badge over notifications the server still considered unread, with nothing to
   * correct it short of a reload. markRead directly above always did this properly; the
   * asymmetry was an oversight rather than a decision.
   *
   * The previous rows are captured rather than recomputed, so restoring cannot invent a
   * read state for a row that was already read before the click.
   */
  const markAllRead = useCallback(async () => {
    // rowsRef, not a capture inside the setRows updater: React decides when that updater
    // runs, and it had not run by the time the request came back — the restore put an
    // empty list on screen. The ref is already kept for markRead's own lookup.
    const previous = rowsRef.current;

    setRows((current) => current.map((row) => ({ ...row, read: true })));
    setSharedRows((current) => current.map((row) => ({ ...row, read: true })));
    setUnreadCount(0);
    setUnseenCount(0);

    const controller = new AbortController();
    const response = await request.post<
      { unreadCount: number },
      { message?: string }
    >('/notifications/read_all', {}, controller);

    if (response.type === 'success' && response.data) {
      // Trusted over the assumed zero: rows can arrive between the click and the write.
      setUnreadCount(response.data.unreadCount);
      return;
    }

    // A superseded request is nobody's answer, the same as everywhere else here.
    if (response.type === 'abort') return;

    setRows(previous);
    setSharedRows(previous);
    setUnreadCount(previous.filter((row) => !row.read).length);
  }, [setSharedRows, setUnreadCount, setUnseenCount]);

  return {
    rows,
    totalCount,
    isLoading,
    error,
    channel,
    fetchPage,
    markAllSeen,
    markRead,
    markAllRead,
  };
}
